import {
  Dialog,
  
  FormDatePicker,
  FormInput,
  FormPhoneInput,
  FormSectionHeader,
  FormSelect,
  Heading,
  Loader,
} from "@/components";
import useAddEdit from "./useAddEdit";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
} from "@/components/ui";
import { FormProvider } from "react-hook-form";
import {
  ADDRESS,
  ADDRESS_1,
  ADDRESS_2,
  ALTERNATE_LOGIN_ID,
  CITY,
  COMMUNITY,
  COMMUNITY_NAME,
  DOB,
  EMAIL_ID,
  END_DATE,
  FIRST_NAME,
  LAST_NAME,
  MOBILE_NO,
  ORGANIZATION,
  organizationOptions,
  PO_BOX,
  ROLE,
  START_DATE,
  STATE,
  USERNAME,
  ZIPCODE,
} from "./consts";
import { Bookmark, PencilIcon, Trash2, X } from "lucide-react";
import {
  cn,
  permissionMatrixTitles,
  ROLE_CAM,
  ROLE_CAM_ACCOUNTANT,
  rolesOptions,
} from "@/lib/utils";

const AddEdit = () => {
  const {
    title,
    onBackClick,
    breadcrumbData,
    isView,
    form,
    requiredFields,
    onCancel,
    onSubmit,
    stateList,
    keys,
    communityList,
    isCommunityLoading,
    onAddMore,
    openTabs,
    onAccordionChange,
    isAddEmployeePending,
    onDeleteCommunityRole,
    communityAccess,
    isAdd,
    onDeleteEmployee,
    openDeleteConfirmation,
    setOpenDeleteConfirmation,
    onDeleteConfirm,
    isDeleteEmployeePending,
    onEditIconClick,
    isUpdateEmployeePending,
    isEditBasicDtl,
    isEditCommunity,
    id,
    onCommunityEdit,
    onCancelCommunityEdit,
    onCancelBasicDtlEdit,
    isUpdateAffiliationPending,
    onDeleteRoleClick,
    openDeleteRoleConfirmation,
    setOpenDeleteRoleConfirmation,
    isDeleteEmployeeRolePending,
    onDeleteWhileAdd,
    isUserPermissionLoading,
    auditLog,
    isAuditLoading,
    onPhoneCountryChange,
    formatDescription,
    countryCode,
  } = useAddEdit();
  const { errors } = form.formState;

  return (
    <div className="p-4 md:p-6 !pt-0">
      <div className="flex flex-col lg:flex-row lg:items-center w-full justify-between">
        <div className="flex flex-row items-center justify-between">
          <Heading
            title={title}
            showBack
            onBackClick={onBackClick}
            breadcrumbData={breadcrumbData}
          />
        </div>
        {id ? (
          <Button
            className="bg-red-600 hover:bg-red-600"
            onClick={onDeleteEmployee}
          >
            Delete
          </Button>
        ) : (
          <></>
        )}
      </div>

      {isCommunityLoading || isUserPermissionLoading || isAuditLoading ? (
        <Loader />
      ) : (
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="bg-white mt-[20px] rounded-lg px-[30px] py-[25px]">
              <div>
                <div className="flex flex-row items-center justify-between mb-5">
                  <FormSectionHeader>Basic Information</FormSectionHeader>
                  {isEditBasicDtl ? (
                    <div className="flex flex-row items-center gap-3">
                      <Button
                        variant="outline"
                        className="cursor-pointer"
                        type="button"
                        disabled={isUpdateEmployeePending}
                        onClick={onCancelBasicDtlEdit}
                      >
                        <X className="h-5" />
                      </Button>
                      <Button
                        className="cursor-pointer"
                        type="button"
                        disabled={isUpdateEmployeePending}
                        onClick={form.handleSubmit(onSubmit)}
                      >
                        {isUpdateEmployeePending ? (
                          <Loader variant="secondary" />
                        ) : (
                          <Bookmark className="h-5" />
                        )}
                      </Button>
                    </div>
                  ) : isView ? (
                    <Button
                      variant="outline"
                      type="button"
                      className="cursor-pointer"
                      onClick={onEditIconClick}
                    >
                      <PencilIcon className="h-5" />
                    </Button>
                  ) : (
                    <></>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-[30px]">
                  <FormInput
                    name={USERNAME}
                    control={form.control}
                    label="User ID"
                    placeholder="Please enter"
                    error={errors[USERNAME]?.message}
                    isRequired={requiredFields.includes(USERNAME)}
                    disabled={isView || isEditCommunity}
                    noSpace
                  />
                  <FormInput
                    name={ALTERNATE_LOGIN_ID}
                    control={form.control}
                    label="Alternative Login ID"
                    placeholder="Please enter"
                    error={errors[ALTERNATE_LOGIN_ID]?.message}
                    isRequired={requiredFields.includes(ALTERNATE_LOGIN_ID)}
                    disabled={isView || isEditCommunity}
                    noSpace
                  />
                  <FormInput
                    name={FIRST_NAME}
                    control={form.control}
                    label="First Name"
                    placeholder="Please enter"
                    error={errors[FIRST_NAME]?.message}
                    isRequired={requiredFields.includes(FIRST_NAME)}
                    disabled={isView || isEditCommunity}
                    maxLength={50}
                  />
                  <FormInput
                    name={LAST_NAME}
                    control={form.control}
                    label="Last Name"
                    placeholder="Please enter"
                    error={errors[LAST_NAME]?.message}
                    isRequired={requiredFields.includes(LAST_NAME)}
                    disabled={isView || isEditCommunity}
                    maxLength={50}
                  />
                  <FormPhoneInput
                    name={MOBILE_NO}
                    control={form.control}
                    label="Mobile No"
                    placeholder="Please enter"
                    error={errors[MOBILE_NO]?.message}
                    isRequired={requiredFields.includes(MOBILE_NO)}
                    disabled={isView || isEditCommunity}
                    onlyCountries={["us", "in"]}
                    disableDropdown={isView || isEditCommunity}
                    disableCountryCode={isView || isEditCommunity}
                    disableCountryGuess={isView || isEditCommunity}
                    onChange={onPhoneCountryChange}
                    country={countryCode}
                  />
                  <FormInput
                    name={EMAIL_ID}
                    control={form.control}
                    label="Email ID"
                    placeholder="Please enter"
                    error={errors[EMAIL_ID]?.message}
                    isRequired={requiredFields.includes(EMAIL_ID)}
                    disabled={isView || isEditCommunity}
                  />
                  <div className="md:col-span-2">
                    <FormInput
                      name={`${ADDRESS}.${ADDRESS_1}`}
                      control={form.control}
                      label="Address 1"
                      placeholder="Please enter"
                      error={errors[`${ADDRESS}.${ADDRESS_1}`]?.message}
                      isRequired={requiredFields.includes(
                        `${ADDRESS}.${ADDRESS_1}`
                      )}
                      disabled={isView || isEditCommunity}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <FormInput
                      name={`${ADDRESS}.${ADDRESS_2}`}
                      control={form.control}
                      label="Address 2"
                      placeholder="Please enter"
                      error={errors[`${ADDRESS}.${ADDRESS_2}`]?.message}
                      isRequired={requiredFields.includes(
                        `${ADDRESS}.${ADDRESS_2}`
                      )}
                      disabled={isView || isEditCommunity}
                    />
                  </div>
                  <FormSelect
                    key={keys.state}
                    name={`${ADDRESS}.${STATE}`}
                    control={form.control}
                    label="State"
                    placeholder="Please select"
                    error={errors[`${ADDRESS}.${STATE}`]?.message}
                    isRequired={requiredFields.includes(`${ADDRESS}.${STATE}`)}
                    options={stateList?.data?.map((data: any) => ({
                      label: data.name,
                      value: data.code,
                    }))}
                    disabled={isView || isEditCommunity}
                  />
                  <FormInput
                    name={`${ADDRESS}.${CITY}`}
                    control={form.control}
                    label="City"
                    placeholder="Please select"
                    error={errors[`${ADDRESS}.${CITY}`]?.message}
                    isRequired={requiredFields.includes(`${ADDRESS}.${CITY}`)}
                    disabled={
                      isView ||
                      !form.watch(`${ADDRESS}.${STATE}`) ||
                      isEditCommunity
                    }
                    variant="char"
                  />
                  <FormInput
                    name={`${ADDRESS}.${ZIPCODE}`}
                    control={form.control}
                    label="Zipcode"
                    placeholder="Please enter"
                    error={errors[`${ADDRESS}.${ZIPCODE}`]?.message}
                    isRequired={requiredFields.includes(
                      `${ADDRESS}.${ZIPCODE}`
                    )}
                    maxLength={10}
                    disabled={isView || isEditCommunity}
                    variant="integerSpeChar"
                  />
                  <FormInput
                    name={`${ADDRESS}.${PO_BOX}`}
                    control={form.control}
                    label="PO Box"
                    placeholder="Please enter"
                    error={errors[`${ADDRESS}.${PO_BOX}`]?.message}
                    isRequired={requiredFields.includes(`${ADDRESS}.${PO_BOX}`)}
                    minLength={3}
                    maxLength={3}
                    variant="integer"
                    disabled={isView || isEditCommunity}
                  />
                  <FormDatePicker
                    name={`${DOB}`}
                    control={form.control}
                    label="DOB"
                    placeholder="Please select"
                    error={errors[`${DOB}`]?.message}
                    isRequired={requiredFields.includes(`${DOB}`)}
                    disabled={isView || isEditCommunity}
                    maxDate={new Date()}
                  />
                  <FormSelect
                    key={keys.organization}
                    name={`${ORGANIZATION}`}
                    control={form.control}
                    label="Organization"
                    placeholder="Please select"
                    error={errors[`${ORGANIZATION}`]?.message}
                    isRequired={requiredFields.includes(`${ORGANIZATION}`)}
                    options={organizationOptions}
                    disabled={isView || isEditCommunity}
                  />
                </div>
              </div>
            </div>
            <div className="bg-white mt-[20px] rounded-lg px-[30px] py-[25px]">
              <div>
                <div className="flex flex-row items-center justify-between mb-5">
                  <FormSectionHeader>Community Affiliation</FormSectionHeader>
                  {isEditCommunity ? (
                    <div className="flex flex-row items-center gap-3">
                      <Button
                        variant="outline"
                        className="cursor-pointer"
                        type="button"
                        disabled={isUpdateAffiliationPending}
                        onClick={onCancelCommunityEdit}
                      >
                        <X className="h-5" />
                      </Button>
                      <Button
                        className="cursor-pointer"
                        type="button"
                        disabled={isUpdateAffiliationPending}
                        onClick={form.handleSubmit(onSubmit)}
                      >
                        {isUpdateAffiliationPending ? (
                          <Loader variant="secondary" />
                        ) : (
                          <Bookmark className="h-5" />
                        )}
                      </Button>
                    </div>
                  ) : isView ? (
                    <Button
                      variant="outline"
                      type="button"
                      className="cursor-pointer"
                      onClick={onCommunityEdit}
                    >
                      <PencilIcon className="h-5" />
                    </Button>
                  ) : (
                    <></>
                  )}
                </div>
                <div
                  className={cn(
                    "grid grid-cols-1 md:grid-cols-2 gap-[30px]",
                    id ? "lg:grid-cols-5" : "lg:grid-cols-4"
                  )}
                >
                  {form.watch(COMMUNITY)?.map((_: any, index: any) => {
                    return (
                      <>
                        <FormSelect
                          key={keys.community}
                          name={`${COMMUNITY}.${index}.${COMMUNITY_NAME}`}
                          control={form.control}
                          label="Community Name"
                          placeholder="Please select"
                          error={
                            (errors[COMMUNITY] as any)?.[`${index}`]?.[
                              COMMUNITY_NAME
                            ]?.message
                          }
                          isRequired
                          options={communityList?.data?.map((data: any) => ({
                            label: data.name,
                            value: data.communityId?.toString(),
                          }))}
                          disabled={
                            isView ||
                            isEditBasicDtl ||
                            form.watch(`${COMMUNITY}.${index}.${END_DATE}`)
                          }
                        />
                        <FormSelect
                          key={keys.role}
                          name={`${COMMUNITY}.${index}.${ROLE}`}
                          control={form.control}
                          label="Role"
                          placeholder="Please select"
                          error={
                            (errors[COMMUNITY] as any)?.[`${index}`]?.[ROLE]
                              ?.message
                          }
                          isRequired
                          options={rolesOptions}
                          disabled={
                            isView ||
                            isEditBasicDtl ||
                            form.watch(`${COMMUNITY}.${index}.${END_DATE}`)
                          }
                        />
                        <FormDatePicker
                          name={`${COMMUNITY}.${index}.${START_DATE}`}
                          control={form.control}
                          label="Role Start Date"
                          placeholder="Please select"
                          error={
                            (errors[COMMUNITY] as any)?.[`${index}`]?.[
                              START_DATE
                            ]?.message
                          }
                          isRequired={requiredFields.includes(
                            `${COMMUNITY}[].${START_DATE}`
                          )}
                          disabled={
                            isView ||
                            isEditBasicDtl ||
                            form.watch(`${COMMUNITY}.${index}.${END_DATE}`)
                          }
                          onClear={() =>
                            form.setValue(
                              `${COMMUNITY}.${index}.${START_DATE}`,
                              ""
                            )
                          }
                        />
                        {id && (
                          <FormDatePicker
                            name={`${COMMUNITY}.${index}.${END_DATE}`}
                            control={form.control}
                            label="Role End Date"
                            placeholder="Please select"
                            error={
                              (errors[COMMUNITY] as any)?.[`${index}`]?.[
                                END_DATE
                              ]?.message
                            }
                            isRequired={requiredFields.includes(
                              `${COMMUNITY}[].${END_DATE}`
                            )}
                            disabled={
                              isView ||
                              isAdd ||
                              isEditBasicDtl ||
                              form.watch(`${COMMUNITY}.${index}.${END_DATE}`)
                            }
                            minDate={form.watch(
                              `${COMMUNITY}.${index}.${START_DATE}`
                            )}
                            onClear={() =>
                              form.setValue(
                                `${COMMUNITY}.${index}.${END_DATE}`,
                                ""
                              )
                            }
                          />
                        )}
                        {isEditCommunity ? (
                          !form.watch(`${COMMUNITY}.${index}.${END_DATE}`) ? (
                            <Button
                              variant="ghost"
                              onClick={() => onDeleteRoleClick(index)}
                              type="button"
                            >
                              <Trash2 className="stroke-red-600" />
                            </Button>
                          ) : isAdd ? (
                            <Button
                              variant="ghost"
                              onClick={() => onDeleteWhileAdd(index)}
                              type="button"
                            >
                              <Trash2 className="stroke-red-600" />
                            </Button>
                          ) : (
                            <div></div>
                          )
                        ) : (
                          <div></div>
                        )}
                      </>
                    );
                  })}
                </div>
                <Button
                  variant="link"
                  type="button"
                  className="p-0 mt-4"
                  onClick={onAddMore}
                  disabled={!!isView || !!isEditBasicDtl}
                >
                  + Add More
                </Button>
              </div>
            </div>
            {isAdd && (
              <div className="flex flex-row justify-end gap-3  py-3">
                <Button variant="outline" type="button" onClick={onCancel}>
                  Cancel
                </Button>

                <Button type="submit" disabled={isAddEmployeePending}>
                  {" "}
                  {isAddEmployeePending ? (
                    <Loader variant="secondary" />
                  ) : (
                    <></>
                  )}{" "}
                  Save
                </Button>
              </div>
            )}
          </form>
        </FormProvider>
      )}
      {id ? (
        <>
          <div className="mt-7">
            <FormSectionHeader>Community Level Access</FormSectionHeader>
            <Accordion
              type="multiple"
              value={openTabs}
              onValueChange={onAccordionChange}
            >
              {communityAccess?.map((communityData: any, index: any) => {
                return (
                  <AccordionItem
                    value={`${communityData?.communityId}${index}`}
                    className="bg-white px-5 rounded-lg mb-[20px] shadow-md"
                  >
                    <AccordionTrigger className="cursor-pointer">
                      {communityData?.communityName}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-[30px] py-4 items-stretch">
                        {Object.entries(communityData?.permissions)?.map(
                          ([moduleName, actions]) => {
                            const labels =
                              permissionMatrixTitles[
                                moduleName as keyof typeof permissionMatrixTitles
                              ];

                            return (
                              <div className="flex flex-col rounded-[6px] overflow-hidden h-full">
                                {/* Header */}
                                <div className="bg-primary text-white p-3 text-center">
                                  {moduleName
                                    .toLowerCase()
                                    .replace(/_/g, " ")
                                    .replace(/\b\w/g, (c) => c.toUpperCase())}
                                </div>

                                {/* Body (flex grow so it stretches evenly) */}
                                <div className="bg-primary-300 text-primary py-3 px-5 flex-1">
                                  {labels?.map((perm: any) => (
                                    <div className="flex flex-row justify-between my-2">
                                      <span>{perm?.label || ""}</span>
                                      <span>
                                        {Object.keys(actions as any)?.includes(
                                          perm?.value
                                        )
                                          ? "Yes"
                                          : "No"}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          }
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>

          <div className="bg-white mt-[20px] rounded-lg px-[30px] py-[25px]">
            <label>Audit History</label>
            <div className="mt-3 max-h-[360px] overflow-y-auto">
              {auditLog?.data?.length ? (
                auditLog?.data?.map((log: any, index: number) => {
                  return (
                    <p key={`audit-${index + 1}`} className="text-[12px]">
                      {index + 1}. On{" "}
                      <b className="font-[600]">{log?.eventDate ?? ""}</b> -
                      {log?.entityType == ROLE_CAM_ACCOUNTANT
                        ? `Role Cam-A ${formatDescription(
                            log?.description
                          )} for Community ${log?.communityName}`
                        : log?.entityType == ROLE_CAM
                        ? `Role Cam ${formatDescription(
                            log?.description
                          )} for Community ${log?.communityName}`
                        : `Employee ${formatDescription(log?.description)}`}
                    </p>
                  );
                })
              ) : (
                <p className="text-[14px]">No Logs</p>
              )}
            </div>
          </div>
        </>
      ) : (
        <></>
      )}
      {openDeleteConfirmation && (
        <Dialog
          open={openDeleteConfirmation}
          setOpen={setOpenDeleteConfirmation}
          title={
            <div className="text-2xl font-bold text-primary">
              {"Confirmation"}
              <div className="h-1 w-[30%] bg-primary rounded-full" />
            </div>
          }
          onSave={onDeleteConfirm}
          saveText="Delete"
          saveBtnClassName="bg-red-600 hover:bg-red-600"
          isLoading={isDeleteEmployeePending}
        >
          <div className="relative">
            {`This employee is currently associated with Community `}
            <b>
              {form
                .watch(COMMUNITY)
                .map((data: any) => data.name)
                ?.join(", ")}
            </b>{" "}
            {`, holding the roles.`}{" "}
            <b>
              {form
                .watch(COMMUNITY)
                .map(
                  (data: any) =>
                    rolesOptions.find((val: any) => val.value == data.role)
                      ?.label
                )
                ?.join(", ")}
            </b>
            <br />
            <br />
            {`Deleting this employee will remove them from all associated communities and revoke their
            existing roles.`}
            <br />
            <br />
            {`Are you sure you want to proceed with this deletion?`}
          </div>
        </Dialog>
      )}

      {openDeleteRoleConfirmation && (
        <Dialog
          open={openDeleteRoleConfirmation}
          setOpen={setOpenDeleteRoleConfirmation}
          title={
            <div className="text-2xl font-bold text-primary">
              {"Confirmation"}
              <div className="h-1 w-[30%] bg-primary rounded-full" />
            </div>
          }
          onSave={onDeleteCommunityRole}
          saveText="Delete"
          saveBtnClassName="bg-red-600 hover:bg-red-600"
          isLoading={isDeleteEmployeeRolePending}
        >
          <div className="relative">
            {`Are you sure you want to delete this role?`}
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default AddEdit;
